export type ArrayMerge<A extends unknown[]> = UnionToIntersection<A[number]>;

export type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

export type NullishPartial<T> = { [P in keyof T]?: T[P] | null };

/** Probably useful for fixing mongoose / model type incompatibilities */
export type DeepOptionalToNullish<T> =
  T extends Array<infer U>
    ? Array<DeepOptionalToNullish<U>>
    : T extends object
      ? {
          [K in keyof T]: undefined extends T[K]
            ? DeepOptionalToNullish<Exclude<T[K], undefined>> | null | undefined
            : DeepOptionalToNullish<T[K]>;
        }
      : T;

export type Optional<T> = T | undefined | null;

export type PartialFields<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;

export type Primitive = string | number | boolean | null | undefined;

// https://github.com/microsoft/TypeScript/issues/31501#issuecomment-1280579305
export type OmitUnion<T, K extends keyof any> = T extends any
  ? Omit<T, K>
  : never;

export type PartialExcept<T, K extends keyof T> = Partial<T> &
  Required<Pick<T, K>>;

export type WithRequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

export type WithRequiredNonNullableFields<T, K extends keyof T> = T & {
  [P in K]-?: NonNullable<T[P]>;
};

export type ArrayNonNullable<T> =
  T extends Array<infer U> ? Array<NonNullable<U>> : never;

export type NonEmptyArray<T> = [T, ...T[]];

export function isNonEmptyArray<T>(arr: T[]): arr is NonEmptyArray<T> {
  return arr.length > 0;
}

export type UnionToIntersection<U> = (
  U extends any ? (k: U) => never : never
) extends (k: infer I) => never
  ? I
  : never;

export type AllOrNone<T> = T | { [K in keyof T]?: never };

export function isString(v: unknown): v is string {
  return typeof v === "string";
}

export function isDefined<T>(v: T | undefined | null): v is T {
  return v !== undefined && v !== null;
}

export function assertNever(value: never, errorMessage?: string): never {
  throw new Error(errorMessage ?? `Unexpected value: ${value}`);
}

export function safeAssertNever(value: never): never {
  return value as never;
}

export function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

export function isIterable<T>(v: unknown): v is Iterable<T> {
  return (
    typeof v === "object" &&
    v !== null &&
    (typeof (v as any)[Symbol.iterator] === "function" ||
      typeof (v as any).next === "function")
  );
}

export type TypedKeyedObject<T extends string, V> = { [K in T]?: V };

export type Tuple<T, TLength extends number> = [T, ...T[]] & {
  length: TLength;
};

export type Falsy = false | 0 | "" | null | undefined;

/**
 * Lets the compiler know that the value is unused but we are ok with that.
 */
export function sinkValue<T>(value: T): void {
  // noop
}

type BuildRangeTuple<
  Current extends [...number[]],
  Count extends number,
> = Current["length"] extends Count
  ? Current
  : BuildRangeTuple<[number, ...Current], Count>;

type RangeTuple<Count extends number> = BuildRangeTuple<[], Count>;

type BuildRange<
  Current extends number,
  End extends number,
  Accu extends [...number[]],
> = Accu["length"] extends End
  ? Current
  : BuildRange<Current | Accu["length"], End, [number, ...Accu]>;

export type NumberRange<
  StartInclusive extends number,
  EndExclusive extends number,
> = BuildRange<StartInclusive, EndExclusive, RangeTuple<StartInclusive>>;

/**
 * Manifest, for covariant type
 */
export class Type<T> {
  _type!: T;

  wrap(value: T): any {
    return new TypedValue(this, value);
  }

  unwrap(typedValue: any): T {
    if (!(typedValue instanceof TypedValue)) {
      throw new Error("Expected typed value");
    }
    if (typedValue.type !== this) {
      throw new Error("Type mismatch");
    }
    return typedValue.value;
  }
}

export class TypedValue<T> {
  constructor(
    readonly type: Type<T>,
    readonly value: T,
  ) {}
}

type MaxDepth = 7;

export type LeafFieldPath<
  T,
  Depth extends number[] = [],
  Prefix extends string = "",
> = Depth["length"] extends MaxDepth
  ? never
  : T extends object
    ? {
        [K in keyof T]: T[K] extends object
          ? T[K] extends Array<any>
            ? `${Prefix}${K & string}`
            : LeafFieldPath<T[K], [...Depth, 1], `${Prefix}${K & string}.`>
          : `${Prefix}${K & string}`;
      }[keyof T]
    : never;

export type RecordKey<R> = R extends Record<infer K, any> ? K : never;
export type RecordValue<R> = R extends Record<any, infer V> ? V : never;
export type RecordEntry<R> =
  R extends Record<infer K, infer V> ? [K, V] : never;

export type LeafFieldPathValue<
  T,
  Path extends LeafFieldPath<T>,
  Depth extends number[] = [],
> = Depth["length"] extends MaxDepth
  ? never
  : T extends object
    ? Path extends `${infer FIRST extends keyof T & string}.${infer REST}`
      ? REST extends LeafFieldPath<T[FIRST]>
        ? LeafFieldPathValue<T[FIRST], REST, [...Depth, 1]>
        : never
      : Path extends keyof T
        ? T[Path]
        : never
    : never;

type LeafFieldWithValueHelper<T, K> =
  K extends LeafFieldPath<T> ? [K, LeafFieldPathValue<T, K>] : never;

/** Given an object type, returns a union of array types
 * each of which consists of a leaf field path for T
 * paired with the type of that leaf field */
export type LeafFieldWithValue<T> = LeafFieldWithValueHelper<
  T,
  LeafFieldPath<T>
>;

export type Value<T> = T[keyof T];

export type DeepReadonly<T> = {
  readonly [P in keyof T]: T[P] extends object ? DeepReadonly<T[P]> : T[P];
};

export type XorOfUnions<T, U> = Exclude<T, U> | Exclude<U, T>;

export type ArrayLengthLessThan<N extends number, T = unknown> = T[] & {
  length: NumberRange<0, N>;
};

type PathsToObjectTypeHelper<
  Paths extends string,
  Value = any,
  Depth extends number[] = [],
> = Depth["length"] extends MaxDepth
  ? {}
  : Paths extends `${infer Key}.${infer Rest}`
    ? { [K in Key]: PathsToObjectTypeHelper<Rest, Value, [...Depth, 1]> }
    : Paths extends `${infer Key}[]${infer Rest}`
      ? { [K in Key]: PathsToObjectTypeHelper<Rest, Value, [...Depth, 1]>[] }
      : { [K in Paths]: Value };

/**
 * The reverse of LeafFieldPath.
 * Converts an array of field paths into a nested object type structure.
 * Supports two delimiters:
 * - "." creates nested object fields: "field.bar" → { field: { bar: any } }
 * - "[]" creates array fields: "field[]bar" → { field: { bar: any }[] }
 * For example: ["field.bar", "field[]baz"] becomes { field: { bar: any, baz: any[] } }
 */
export type PathsToObjectType<
  Paths extends readonly string[],
  Value = any,
> = UnionToIntersection<PathsToObjectTypeHelper<Paths[number], Value>>;
